import { Amplify } from 'aws-amplify';
import { Hub } from 'aws-amplify/utils';
import { signInWithRedirect, signOut, getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import React, { createContext, ReactNode, useCallback, useEffect, useState } from 'react';
import { usersApi } from '@/services/api/users';
import { useConfig } from './ConfigContext';

// User model
export interface WeibullUser {
  id: string;
  email: string;
  fullName: string;
  givenName?: string;
  familyName?: string;
  isOnyxUser: boolean;
  company: 'onyx' | 'company';
  role: 'admin' | 'user';
}

export enum AuthState {
  Initializing = 'INITIALIZING',
  Authenticated = 'AUTHENTICATED',
  Unauthenticated = 'UNAUTHENTICATED',
  TokenRefreshFailed = 'TOKEN_REFRESH_FAILED',
  Error = 'ERROR',
}

// Context interface
interface CognitoAuthContextType {
  state: AuthState;
  user: WeibullUser | null;
  error: string | null;
  login: () => void;
  logout: () => void;
  getToken: () => Promise<string>;
  refreshToken: () => Promise<void>;
  refreshUserPermissions: () => Promise<void>;
}

// Create context with default values
export const CognitoAuthContext = createContext<CognitoAuthContextType>({
  state: AuthState.Initializing,
  user: null,
  error: null,
  login: () => {
    console.error('AuthContext used outside of provider');
  },
  logout: () => {
    console.error('AuthContext used outside of provider');
  },
  getToken: async () => {
    console.error('AuthContext used outside of provider');
    return '';
  },
  refreshToken: async () => {
    console.error('AuthContext used outside of provider');
  },
  refreshUserPermissions: async () => {
    console.error('AuthContext used outside of provider');
  },
});

CognitoAuthContext.displayName = 'CognitoAuthContext';

// Create user object from auth session and backend
const createUserFromSession = async (): Promise<WeibullUser> => {
  const { userId } = await getCurrentUser();
  const session = await fetchAuthSession();
  const idToken = session.tokens?.idToken;

  if (!idToken) {
    throw new Error('No valid session token');
  }

  // Parse JWT payload for basic info
  const payload = idToken.payload;
  const email = (payload.email as string) || '';

  // Get user data from backend - this WILL throw if not authenticated
  const backendUser = await usersApi.getMe();

  // Use backend data as source of truth
  const company = backendUser.company?.toLowerCase() === 'onyx' ? 'onyx' : 'company';
  const role = backendUser.role?.toLowerCase() === 'admin' ? 'admin' : 'user';

  return {
    id: backendUser.user_id || userId || (payload.sub as string),
    email: backendUser.email || email,
    fullName: `${backendUser.first_name || ''} ${backendUser.last_name || ''}`.trim() || email,
    givenName: backendUser.first_name || (payload.given_name as string),
    familyName: backendUser.last_name || (payload.family_name as string),
    isOnyxUser: company === 'onyx',
    company,
    role,
  };
};

// Auth Provider Component
export function CognitoAuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const { config } = useConfig();
  const [state, setState] = useState<AuthState>(AuthState.Initializing);
  const [user, setUser] = useState<WeibullUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isConfigured, setIsConfigured] = useState(false);

  // Configure Amplify when config is available
  useEffect(() => {
    if (!config) {
      return; // Wait for config to be loaded
    }

    // Validate required config
    const required = [
      'REACT_APP_COGNITO_USER_POOL_ID',
      'REACT_APP_COGNITO_USER_POOL_CLIENT_ID',
      'REACT_APP_COGNITO_DOMAIN',
    ];

    const missing = required.filter((key) => !config[key as keyof typeof config]);

    if (missing.length > 0) {
      console.error('Missing required auth configuration:', missing);
      setState(AuthState.Error);
      setError('Invalid authentication configuration');
      return;
    }

    // Configure Amplify with the loaded config
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: config.REACT_APP_COGNITO_USER_POOL_ID,
          userPoolClientId: config.REACT_APP_COGNITO_USER_POOL_CLIENT_ID,
          loginWith: {
            oauth: {
              domain: config.REACT_APP_COGNITO_DOMAIN,
              scopes: ['email', 'openid', 'profile'],
              redirectSignIn: [config.REACT_APP_COGNITO_REDIRECT_SIGN_IN],
              redirectSignOut: [config.REACT_APP_COGNITO_REDIRECT_SIGN_OUT],
              responseType: 'code' as const,
            },
          },
        },
      },
    });

    setIsConfigured(true);
  }, [config]);

  // Setup Auth Hub listener
  useEffect(() => {
    if (!isConfigured) {
      return; // Wait for Amplify to be configured
    }

    const hubListener = Hub.listen('auth', async (data) => {
      const { event } = data.payload;

      switch (event) {
        case 'signedIn':
        case 'signInWithRedirect':
          try {
            // Cognito sign-in successful, now get backend data
            const newUser = await createUserFromSession();
            setUser(newUser);
            setError(null);
            setState(AuthState.Authenticated);
          } catch (err) {
            console.error('Failed to verify user with backend:', err);
            setError(err instanceof Error ? err.message : 'Failed to verify user permissions');
            setState(AuthState.Error);
            setUser(null);
          }
          break;

        case 'tokenRefresh':
          break;

        case 'tokenRefresh_failure':
          console.error('Token refresh failed');
          setError('Session expired. Please sign in again.');
          setState(AuthState.TokenRefreshFailed);
          setUser(null);
          break;

        case 'signedOut':
          setUser(null);
          setError(null);
          setState(AuthState.Unauthenticated);
          break;

        case 'signInWithRedirect_failure':
        case 'customOAuthState':
          setError('Authentication failed');
          setState(AuthState.Error);
          setUser(null);
          break;
      }
    });

    return () => hubListener();
  }, [isConfigured]);

  // Check current auth state on mount
  useEffect(() => {
    if (!isConfigured) {
      return; // Wait for Amplify to be configured
    }

    // Set initializing state
    setState(AuthState.Initializing);

    // Check for OAuth errors first
    const urlParams = new URLSearchParams(window.location.search);
    const error = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');

    if (error) {
      setError(`Authentication error: ${errorDescription || error}`);
      setState(AuthState.Error);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    // Now check auth state
    checkAuthState();
  }, [isConfigured]);

  const checkAuthState = async () => {
    try {
      // First, check if user has Cognito session
      const currentUser = await getCurrentUser();

      if (!currentUser) {
        // No Cognito session - user is not authenticated
        setState(AuthState.Unauthenticated);
        setUser(null);
        setError(null);
        return; // Exit early - no backend call
      }

      // User has Cognito session, now verify with backend
      try {
        const newUser = await createUserFromSession();
        setUser(newUser);
        setState(AuthState.Authenticated);
        setError(null);
      } catch (backendError) {
        // Backend verification failed
        console.error('Backend verification failed:', backendError);
        setError(
          backendError instanceof Error ? backendError.message : 'Failed to verify user permissions'
        );
        setState(AuthState.Error);
        setUser(null);
      }
    } catch (cognitoError) {
      // Cognito check failed - treat as unauthenticated
      setState(AuthState.Unauthenticated);
      setUser(null);
      setError(null);
    }
  };

  const login = useCallback(() => {
    if (!isConfigured) {
      setError('Authentication not configured');
      return;
    }

    try {
      signInWithRedirect();
    } catch (err) {
      setError('Failed to initiate login');
    }
  }, [isConfigured]);

  // Logout
  const logout = useCallback(async () => {
    try {
      await signOut();
      setUser(null);
      setError(null);
    } catch (err) {
      setError('Failed to sign out');
    }
  }, []);

  // Get current access token
  const getToken = useCallback(async (): Promise<string> => {
    try {
      const session = await fetchAuthSession();
      const accessToken = session.tokens?.accessToken;
      if (!accessToken) {
        throw new Error('No access token available');
      }
      return accessToken.toString();
    } catch (err) {
      throw new Error('No valid session');
    }
  }, []);

  // Manually refresh token
  const refreshToken = useCallback(async () => {
    const session = await fetchAuthSession({ forceRefresh: true });
    if (!session.tokens?.accessToken) {
      throw new Error('Failed to refresh token');
    }
  }, []);

  // Refresh user permissions from backend
  const refreshUserPermissions = useCallback(async () => {
    try {
      // Ensure we have a valid Cognito session first
      const currentUser = await getCurrentUser();
      if (!currentUser) {
        throw new Error('No authenticated user');
      }

      // Fetch updated user data from backend
      const updatedUser = await createUserFromSession();
      setUser(updatedUser);
      setError(null);
      setState(AuthState.Authenticated);
    } catch (err) {
      console.error('Failed to refresh user permissions:', err);

      // If backend returns 401/403, the user is no longer authorized
      if (err instanceof Error && err.message.includes('401')) {
        setError('Your session has expired. Please sign in again.');
        setState(AuthState.Unauthenticated);
        setUser(null);
        // Don't throw - let the API client handle the redirect
      } else {
        // For other errors, update the error state but keep the user logged in
        setError('Failed to refresh permissions. Some features may be unavailable.');
        // Re-throw to let the caller know refresh failed
        throw err;
      }
    }
  }, []);

  return (
    <CognitoAuthContext.Provider
      value={{
        state,
        user,
        error,
        login,
        logout,
        getToken,
        refreshToken,
        refreshUserPermissions,
      }}
    >
      {children}
    </CognitoAuthContext.Provider>
  );
}
